import { useCallback, useId, useRef, useState } from 'react';
import { isEntraConfigured } from '../../auth/entraConfig';
import { FEATURES } from '../../config/features';
import { saveBlob } from '../custodian/saveBlob';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { MfaExportDialog } from './MfaExportDialog';

const COPY = {
  provider: {
    APPROVE: {
      title: 'Approve selected packages',
      confirmLabel: 'Approve',
      description:
        'Mark each selected package as a Provider CANDIDATE. This records eligibility for Custodian routing. It does not approve legal authority and it does not move custody.',
    },
    REJECT: {
      title: 'Discard selected packages',
      confirmLabel: 'Discard',
      description:
        'Return each selected package to the originator. A package already routed to the Custodian is left unchanged and reported on its own.',
    },
    TRANSFER: {
      title: 'Transfer selected packages',
      confirmLabel: 'Transfer',
      description:
        'Route each selected CANDIDATE package to the Custodian. Approve a package first when it is not yet a CANDIDATE. This hands the package to the Custodian and does not move custody.',
    },
  },
  custodian: {
    APPROVE: {
      title: 'Approve selected packages',
      confirmLabel: 'Approve',
      description:
        'Record Custodian authority as APPROVED. Vault admission is still required before custody is accepted.',
    },
    REJECT: {
      title: 'Discard selected packages',
      confirmLabel: 'Discard',
      description:
        'Record Custodian authority as REJECTED. These packages will not be admitted to the Authoritative vault.',
    },
    TRANSFER: {
      title: 'Transfer selected packages',
      confirmLabel: 'Transfer',
      description:
        'Accept each locked transfer on the selected packages and commit it. Commit writes the next authority version and a receipt. A package with no waiting transfer is left unchanged and reported on its own.',
    },
  },
};

function CheckIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M3.5 8.2 6.4 11l6.1-6.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CrossIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 4l8 8M12 4l-8 8" strokeLinecap="round" />
    </svg>
  );
}

function TransferIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M2.5 8h11M9.5 4.5 13 8l-3.5 3.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6">
      <rect x="3.5" y="7" width="9" height="6.5" rx="1.2" />
      <path d="M5.5 7V5.2a2.5 2.5 0 0 1 5 0V7" strokeLinecap="round" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M8 2.5v7M5.5 7.5 8 10l2.5-2.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3.5 12.5h9" strokeLinecap="round" />
    </svg>
  );
}

export function usePackageSelection() {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggle = useCallback((id) => {
    if (!id) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback((rows, checked, getId) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      rows.forEach((row) => {
        const id = getId(row);
        if (!id) return;
        if (checked) next.add(id);
        else next.delete(id);
      });
      return next;
    });
  }, []);

  const retain = useCallback((idsToDrop) => {
    const drop = new Set(idsToDrop);
    setSelectedIds((current) => {
      const next = new Set(current);
      drop.forEach((id) => next.delete(id));
      return next;
    });
  }, []);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return { selectedIds, toggle, toggleAll, retain, clear, count: selectedIds.size };
}

export function bulkResultMessage(response) {
  const results = Array.isArray(response?.results) ? response.results : [];
  const succeeded = results.filter((row) => row.status === 'succeeded');
  const failed = results.filter((row) => row.status === 'failed');
  const notice = succeeded.length
    ? `${succeeded.length} package${succeeded.length === 1 ? '' : 's'} updated.`
    : '';
  const error = failed.map((row) => row.message).filter(Boolean).join(' ');
  return {
    notice,
    error,
    succeededIds: succeeded.map((row) => row.package_id),
  };
}

const ACTION_BUTTON =
  'inline-flex items-center gap-1.5 rounded-md border bg-white px-3 py-1.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50';

/**
 * Multi-select package actions. `enabled` is false for the read-only Investor
 * workspace: the bar stays visible and no request is sent. `mfaRequired`
 * (default: FEATURES.bulkMfa, off for now) adds the MFA step-up to each confirm.
 */
export function BulkPackageActions({
  role,
  selectedCount = 0,
  exportCount = 0,
  busy = false,
  enabled = true,
  mfaRequired = FEATURES.bulkMfa,
  onAction,
  onExport,
  onImport,
}) {
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null);
  const [modalError, setModalError] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const mfaFieldId = useId();
  const [exportMode, setExportMode] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const [exportError, setExportError] = useState('');
  const copy = enabled ? COPY[role]?.[pending] : null;
  const entra = mfaRequired && isEntraConfigured();
  const locked = !enabled || selectedCount === 0 || busy;
  const exportLocked = !enabled || busy || exportCount === 0;
  const lockedTitle = enabled
    ? selectedCount === 0
      ? 'Select at least one package'
      : undefined
    : 'Investor access is read-only. No action can be started from this workspace.';
  const exportTitle = !enabled
    ? lockedTitle
    : exportCount === 0
      ? 'Select Certified packages with Approved authority. The download is watermarked copies only.'
      : mfaRequired
        ? 'Download watermarked copies. Multi-factor authentication is required.'
        : 'Download watermarked copies.';

  const open = (action) => {
    if (locked) return;
    setModalError('');
    setMfaCode('');
    setPending(action);
  };

  const confirm = async () => {
    if (!pending || !onAction || busy) return;
    setModalError('');
    try {
      await onAction(pending, mfaRequired ? { mfaCode: mfaCode.trim() } : {});
      setPending(null);
    } catch (requestError) {
      setModalError(requestError.message || 'Unable to update the selected packages.');
    }
  };

  // Microsoft sign-in is exchanged for an eVault session token, so the session
  // itself never proves MFA. Ask Microsoft for a fresh MFA token and send that.
  const confirmWithMicrosoft = async () => {
    if (!pending || !onAction || busy) return;
    setModalError('');
    try {
      const { acquireEntraMfaToken } = await import('../../auth/entraClient');
      const mfaToken = await acquireEntraMfaToken();
      await onAction(pending, { mfaToken, mfaCode: mfaCode.trim() });
      setPending(null);
    } catch (requestError) {
      const message = requestError?.message || 'MFA confirmation was cancelled.';
      setModalError(
        /user_cancelled|user canceled|cancelled|canceled/i.test(message)
          ? 'MFA confirmation was cancelled.'
          : message
      );
    }
  };

  const openExport = (mode, file) => {
    if (!enabled || busy) return;
    if (mode === 'selection' && exportLocked) return;
    setExportError('');
    setImportFile(file || null);
    setExportMode(mode);
  };

  const confirmExport = async (mfa) => {
    setExportError('');
    try {
      const result =
        exportMode === 'import' ? await onImport(importFile, mfa) : await onExport(mfa);
      saveBlob(result.blob, result.fileName || 'watermarked-package-copies.zip');
      setExportMode(null);
      setImportFile(null);
    } catch (requestError) {
      setExportError(requestError.message || 'Unable to export watermarked copies.');
    }
  };

  return (
    <section className="rounded-xl border border-[#E5E7EB] bg-white px-4 py-3 shadow-sm">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <div className="min-w-[16rem] flex-1">
          <h2 className="text-sm font-semibold text-[#1F2933]">Bulk actions</h2>
          <p className="text-xs text-[#5A6A7A]">
            Select multiple packages to perform actions in one step.
          </p>
        </div>
        <p className="text-sm text-[#5A6A7A]" data-testid="bulk-selected-count">
          {selectedCount} package{selectedCount === 1 ? '' : 's'} selected
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={`${ACTION_BUTTON} border-emerald-600 text-emerald-700`}
            disabled={locked}
            title={lockedTitle}
            onClick={() => open('APPROVE')}
          >
            <CheckIcon />
            Approve selected
          </button>
          <button
            type="button"
            className={`${ACTION_BUTTON} border-red-400 text-red-600`}
            disabled={locked}
            title={lockedTitle}
            onClick={() => open('REJECT')}
          >
            <CrossIcon />
            Discard selected
          </button>
          <button
            type="button"
            className={`${ACTION_BUTTON} border-[#003B5B] text-[#003B5B]`}
            disabled={locked}
            title={lockedTitle}
            onClick={() => open('TRANSFER')}
          >
            <TransferIcon />
            Transfer selected
          </button>
          {onExport ? (
            <button
              type="button"
              className={`${ACTION_BUTTON} border-[#003B5B] text-[#003B5B]`}
              disabled={exportLocked}
              title={exportTitle}
              onClick={() => openExport('selection')}
            >
              <DownloadIcon />
              Export watermarked copies
            </button>
          ) : null}
          {onImport ? (
            <button
              type="button"
              className={`${ACTION_BUTTON} border-[#003B5B] text-[#003B5B]`}
              disabled={!enabled || busy}
              title={
                enabled
                  ? 'Import a CSV of package ids or references and download watermarked copies.'
                  : lockedTitle
              }
              onClick={() => fileRef.current?.click()}
            >
              Import package list
            </button>
          ) : null}
        </div>
        <p className="ml-auto flex items-center gap-1.5 text-xs text-[#5A6A7A]">
          <LockIcon />
          {mfaRequired
            ? 'Actions require authorized access and MFA confirmation.'
            : 'Actions are recorded against your signed-in account.'}
        </p>
      </div>
      {onImport ? (
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv,text/plain"
          className="sr-only"
          aria-label="Import package list"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) openExport('import', file);
          }}
        />
      ) : null}

      <Modal
        open={Boolean(copy)}
        onClose={busy ? undefined : () => setPending(null)}
        title={copy?.title || 'Confirm bulk action'}
        footer={
          <>
            <Button variant="ghost" type="button" onClick={() => setPending(null)} disabled={busy}>
              Cancel
            </Button>
            {entra ? (
              <Button variant="navy" type="button" onClick={confirmWithMicrosoft} disabled={busy}>
                {busy ? 'Working…' : `${copy?.confirmLabel || 'Confirm'} with Microsoft MFA`}
              </Button>
            ) : null}
            <Button
              variant={entra ? 'ghost' : 'navy'}
              type="button"
              onClick={confirm}
              disabled={busy || (entra && !mfaCode.trim())}
              title={entra && !mfaCode.trim() ? 'Enter a step-up code, or confirm with Microsoft MFA' : undefined}
            >
              {busy ? 'Working…' : entra ? `${copy?.confirmLabel || 'Confirm'} with code` : copy?.confirmLabel || 'Confirm'}
            </Button>
          </>
        }
      >
        <p className="text-sm leading-relaxed text-[#1F2933]">{copy?.description}</p>
        <p className="mt-3 text-sm text-[#5A6A7A]">
          This confirmation is recorded against your signed-in account. Each package follows the
          same rules as a single action.
        </p>
        {mfaRequired ? (
          <>
            <label htmlFor={mfaFieldId} className="mt-4 block text-sm font-semibold text-[#1F2933]">
              MFA code
            </label>
            <input
              id={mfaFieldId}
              type="password"
              autoComplete="one-time-code"
              value={mfaCode}
              onChange={(event) => setMfaCode(event.target.value)}
              aria-label="MFA code"
              className="mt-1 h-9 w-full rounded-md border border-[#D1D5DB] px-3 text-sm"
            />
            <p className="mt-1 text-xs text-[#5A6A7A]">
              {entra
                ? 'Confirm with Microsoft MFA, or enter the step-up code issued for this environment.'
                : 'Enter the step-up code issued for this environment.'}
            </p>
          </>
        ) : null}
        {modalError ? (
          <p className="mt-3 text-sm text-umb-error" role="alert">
            {modalError}
          </p>
        ) : null}
      </Modal>

      <MfaExportDialog
        open={Boolean(exportMode)}
        mfaRequired={mfaRequired}
        mode={exportMode === 'import' ? 'import' : 'selection'}
        busy={busy}
        error={exportError}
        onClose={() => {
          if (busy) return;
          setExportMode(null);
          setImportFile(null);
        }}
        onConfirm={confirmExport}
      />
    </section>
  );
}
